'use client';

import { getCurrentIdToken } from '@/utils/aws/cognito/amplifyAuth';

// GSSP's fast path JWT-verifies the cookie. If the ID token is already expired,
// Amplify fetchAuthSession hits Cognito + PreTokenGeneration (the historical
// "site is slow" window). When the cached exp is within 120s, forceRefresh so
// Amplify does not recache the same near-expiry JWT. An empty in-memory cache
// still reads Amplify without forceRefresh; if that JWT is already in the
// buffer, retry once with forceRefresh. Ordinary reads do not forceRefresh.
// 480-minute Cognito TTL does not remove this; idle timeout and session cap
// still log users out of Hub.
export const COGNITO_ID_TOKEN_REFRESH_BUFFER_SECONDS = 120;
export const COGNITO_SESSION_KEEPALIVE_INTERVAL_MS = 60 * 1000;

let cache = { token: null, exp: null };
let sessionPromise = null;
// Bumped on sign-out so an in-flight keep-alive cannot write the previous
// user's JWT after clearClientIdTokenCache.
let cacheGeneration = 0;

function isExpInRefreshBuffer(exp, nowSec) {
  return exp != null && exp <= nowSec + COGNITO_ID_TOKEN_REFRESH_BUFFER_SECONDS;
}

/**
 * Returns a valid idToken for client-side requests. Uses an in-memory cache
 * and only calls getCurrentIdToken when the token is missing or near
 * expiry (exp < now + 120s). Near-expiry fetches use forceRefresh so Amplify
 * issues a new JWT. If the first Amplify read (empty cache) is already inside
 * the buffer, immediately retry with forceRefresh. Multiple concurrent callers
 * share a single in-flight fetch.
 * @returns {Promise<string|null>} idToken or null if not authenticated
 */
export async function getClientIdToken() {
  // Only call client side.
  if (typeof window === 'undefined') {
    return null;
  }

  const nowSec = Math.floor(Date.now() / 1000);
  const hasCachedToken = Boolean(cache.token) && cache.exp != null;
  const cacheValid =
    hasCachedToken &&
    cache.exp > nowSec + COGNITO_ID_TOKEN_REFRESH_BUFFER_SECONDS;

  if (cacheValid) {
    return cache.token;
  }

  if (sessionPromise) {
    // Joiners capture generation so a later login cannot hand them the next
    // user's JWT. Unchanged generation still reads the live cache so logout
    // during the in-flight fetch cannot return the previous user's JWT.
    const joinerGeneration = cacheGeneration;
    await sessionPromise;
    if (joinerGeneration !== cacheGeneration) {
      return null;
    }
    return cache.token;
  }

  const forceRefresh =
    hasCachedToken &&
    cache.exp <= nowSec + COGNITO_ID_TOKEN_REFRESH_BUFFER_SECONDS;

  const generation = cacheGeneration;
  const thisPromise = (async () => {
    try {
      const { token, exp } = await getCurrentIdToken(
        forceRefresh ? { forceRefresh: true } : undefined
      );
      if (generation !== cacheGeneration) {
        return null;
      }
      let nextToken = token;
      let nextExp = exp ?? null;
      if (
        !forceRefresh &&
        isExpInRefreshBuffer(nextExp, Math.floor(Date.now() / 1000))
      ) {
        const refreshed = await getCurrentIdToken({ forceRefresh: true });
        if (generation !== cacheGeneration) {
          return null;
        }
        if (refreshed.token) {
          nextToken = refreshed.token;
          nextExp = refreshed.exp ?? null;
        } else {
          // Empty retry must not recache a JWT whose exp has already passed.
          // Still-valid near-expiry tokens stay so keep-alive does not log
          // people out 120s early.
          const nowSecRetry = Math.floor(Date.now() / 1000);
          if (nextExp == null || nextExp <= nowSecRetry) {
            nextToken = null;
            nextExp = null;
          }
        }
      }
      if (generation !== cacheGeneration) {
        return null;
      }
      const nowSecAfter = Math.floor(Date.now() / 1000);
      const nextTokenUsable =
        Boolean(nextToken) && nextExp != null && nextExp > nowSecAfter;
      if (nextTokenUsable) {
        cache = { token: nextToken, exp: nextExp };
        return nextToken;
      }
      // Empty, missing-exp, or already-expired: keep a still-unexpired prior
      // JWT. Next call still retries because cacheValid requires exp outside
      // the 120s buffer.
      if (cache.token && cache.exp != null && cache.exp > nowSecAfter) {
        return cache.token;
      }
      cache = { token: null, exp: null };
      return null;
    } catch {
      if (generation === cacheGeneration) {
        const nowSecAfter = Math.floor(Date.now() / 1000);
        if (cache.token && cache.exp != null && cache.exp > nowSecAfter) {
          return cache.token;
        }
        cache = { token: null, exp: null };
      }
      return null;
    } finally {
      if (sessionPromise === thisPromise) {
        sessionPromise = null;
      }
    }
  })();

  sessionPromise = thisPromise;
  return thisPromise;
}

/**
 * Clears the in-memory token cache. Exposed for tests and sign-out flows.
 */
export function clearClientIdTokenCache() {
  cacheGeneration += 1;
  cache = { token: null, exp: null };
  sessionPromise = null;
}

/**
 * Proactive fetchAuthSession from the browser so the next GSSP/apiHandler
 * request still has a valid ID token (JWT fast path) instead of Cognito refresh.
 * Near-expiry calls use forceRefresh; ordinary reads do not. Navigation is
 * not gated on the refresh completing.
 */
export function attachCognitoSessionKeepAlive(router) {
  if (typeof window === 'undefined') {
    return () => {};
  }

  void getClientIdToken();
  const refresh = () => {
    void getClientIdToken();
  };
  router?.events?.on?.('routeChangeStart', refresh);
  const intervalId = setInterval(
    refresh,
    COGNITO_SESSION_KEEPALIVE_INTERVAL_MS
  );
  return () => {
    router?.events?.off?.('routeChangeStart', refresh);
    clearInterval(intervalId);
  };
}
